import { EditIntegrationCTA } from '@modules/integrationsV2/components/EditIntegrationCTA';
import { IntegrationCardDetailsText } from '@modules/integrationsV2/components/IntegrationCardDetailsText';
import { ConnectedIntegrationListActionsMenu } from '@modules/integrationsV2/components/IntegrationsContainer/IntegrationsSourceDetailView/IntegrationSourceConnectedIntegrations/ConnectedIntegrationListActionsMenu';
import { ConnectedIntegrationSecondaryDetails } from '@modules/integrationsV2/components/IntegrationsContainer/IntegrationsSourceDetailView/IntegrationSourceConnectedIntegrations/ConnectedIntegrationSecondaryDetails';
import {
  DataIngestionInProgressIndicator,
  DisabledIntegrationIndicator,
} from '@modules/integrationsV2/components/IntegrationStateIndicator';
import { IntegrationSource } from '@modules/integrationsV2/types';
import {
  getIsEditableIntegration,
  getIsIntegrationIngestionInProgress,
} from '@modules/integrationsV2/utils';
import { getIsIncompleteIntegrationData } from '@modules/integrationsV2/utils/getIsIncompleteIntegrationData';
import {
  ExpandMoreRounded,
  IntegrationInstructionsRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  List,
  ListItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { IntegrationViewFragment } from 'generated/graphql';

interface ConnectedIntegrationListProps {
  integrations: IntegrationViewFragment[];
  integrationSource: IntegrationSource;
}

export const ConnectedIntegrationList = ({
  integrations,
  integrationSource,
}: ConnectedIntegrationListProps) => {
  if (integrationSource.isSingleConnectionSource) {
    return null;
  }

  const renderListItemActions = (integration: IntegrationViewFragment) => {
    const isIncompleteIntegration = getIsIncompleteIntegrationData({
      integrationData: integration.data,
      channelType: integration.channel.type,
      isDisabledIntegration: !integration.isActive,
    });

    const isIngestionInProgress = getIsIntegrationIngestionInProgress({
      onboardingProgress: integration.onboardingProgress,
      isDisabled: !integration.isActive,
      isIncompleteIntegration,
    });

    if (!integration.isActive) {
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          {isIngestionInProgress && (
            <DataIngestionInProgressIndicator variant="chip" />
          )}
          <DisabledIntegrationIndicator variant="chip" />
        </Stack>
      );
    }

    const isEditableIntegration = getIsEditableIntegration({
      channelType: integrationSource.channel.type,
      integrationData: integration.data,
      integrationType: integrationSource.integrationType,
    });

    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        {isIngestionInProgress && (
          <DataIngestionInProgressIndicator variant="chip" />
        )}
        {isIncompleteIntegration && (
          <Stack direction="row" alignItems="center" spacing={0.5} pl={0.5}>
            <WarningAmberRounded
              color="warning"
              sx={{
                fontSize: (theme) => theme.typography.body2.fontSize,
              }}
            />
            <Typography color="warning.main" variant="caption" fontWeight={500}>
              ACTION REQUIRED
            </Typography>
          </Stack>
        )}
        {isEditableIntegration && (
          <EditIntegrationCTA integration={integration} />
        )}
        <ConnectedIntegrationListActionsMenu
          integrationChannel={integration.channel}
          integrationID={integration.id}
          integrationType={integration.integrationType}
          namespace={integration.namespace}
        />
      </Stack>
    );
  };

  const renderListItem = (integration: IntegrationViewFragment) => {
    return (
      <ListItem dense key={integration.id}>
        <Stack width="100%">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                width="22px"
                component="img"
                alignSelf="center"
                src={integration.channel.logo}
                alt={integration.channel.displayName}
              />
              <IntegrationCardDetailsText
                text={integration.externalID || integration.channel.displayName}
                variant="body2"
                maxWidth="400px"
                showClickToCopyTooltip
              />
            </Stack>
            <Box pl={2}>{renderListItemActions(integration)}</Box>
          </Stack>
          <Stack pl={4.2} spacing={1}>
            <ConnectedIntegrationSecondaryDetails integration={integration} />
            {integration.description?.description && (
              <Typography variant="body2" color="text.secondary" width="90%">
                Description: {integration.description.description}
              </Typography>
            )}
          </Stack>
        </Stack>
      </ListItem>
    );
  };

  const allIntegrations = integrations.filter(
    (integration) =>
      integration.channel.type === integrationSource.channel.type &&
      integration.integrationType === integrationSource.integrationType,
  );

  const activeIntegrations = allIntegrations.filter(
    (integration) => integration.isActive,
  );

  const disabledIntegrations = allIntegrations.filter(
    (integration) => !integration.isActive,
  );

  const hasActiveIntegrations = activeIntegrations.length > 0;

  const shouldShowDisabledIntegrationsAccordion =
    hasActiveIntegrations && disabledIntegrations.length > 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        pb: 0.5,
      }}
    >
      <Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IntegrationInstructionsRounded
            fontSize="small"
            sx={{
              color: 'text.secondary',
            }}
          />
          <Typography>Integrations ({allIntegrations.length})</Typography>
        </Stack>
        <List
          dense
          sx={{
            pl: 1.5,
          }}
        >
          {hasActiveIntegrations
            ? activeIntegrations.map((integration) =>
                renderListItem(integration),
              )
            : disabledIntegrations.map((integration) =>
                renderListItem(integration),
              )}
        </List>
        {shouldShowDisabledIntegrationsAccordion && (
          <Box>
            <Divider
              sx={{
                opacity: 0.15,
              }}
            />
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                backgroundColor: 'transparent',
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreRounded
                    sx={{
                      color: 'text.secondary',
                    }}
                  />
                }
                sx={{
                  p: 0,
                  '& .MuiAccordionSummary-expandIconWrapper': {
                    mr: '18px',
                  },
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    pl: '28px',
                  }}
                >
                  Disabled ({disabledIntegrations.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  p: 0,
                }}
              >
                <List
                  dense
                  sx={{
                    pl: 1.5,
                    pt: 0,
                  }}
                >
                  {disabledIntegrations.map((integration) =>
                    renderListItem(integration),
                  )}
                </List>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};
